<template>
  <v-container fluid class="pa-4">
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span class="title">{{ $t('title.orgJedinica') }}</span>
        <div class="d-flex">
          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="mr-2 action-btn"
                icon="mdi-eye" @click="printDepartments" />
            </template>
            {{ $t('general.viewprint') }}
          </v-tooltip>

          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="action-btn" icon="mdi-plus"
                @click="addDepartment" />
            </template>
            {{ $t('general.addDepartment') }}
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text>
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" class="mb-2" density="compact"
          autocomplete="off" />

        <v-data-table :headers="headers" :items="filteredDepartments" :loading="loading"
          :loading-text="$t('general.loading')" class="min-width-table custom-header" :items-per-page="15">

          <!-- Bold naslovi i sort ikonice -->
          <template v-slot:headers="{ columns, toggleSort, isSorted, getSortIcon }">
            <tr>
              <th v-for="column in columns" :key="column.value" @click="() => column.sortable && toggleSort(column)"
                :style="{
                  cursor: column.sortable ? 'pointer' : 'default',
                  width: column.width,
                  maxWidth: column.width,
                  textAlign: column.value === 'actions' ? 'right' : 'left'
                }" class="sortable-header">
                <div class="d-flex align-center w-100" :class="column.value === 'actions'
                  ? 'justify-end'
                  : 'justify-space-between'">
                  <strong>{{ column.text }}</strong>

                  <v-icon v-if="column.sortable" size="12" class="sort-icon ml-2" :class="{ active: isSorted(column) }">
                    {{ isSorted(column) ? getSortIcon(column) : 'mdi-arrow-up-down' }}
                  </v-icon>
                </div>
              </th>
            </tr>
          </template>

          <!-- Naziv -->
          <template v-slot:[`item.Name`]="{ item }">
            {{ item.Name }}
          </template>

          <!-- Akcije -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center justify-end action-buttons">
              <v-tooltip location="bottom" content-class="custom-tooltip">
                <template #activator="{ props }">
                  <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="mr-2 action-btn"
                    @click="editDepartment(item)">
                    <v-icon size="16">mdi-pencil</v-icon>
                  </v-btn>
                </template>
                {{ $t('general.uredi') }}
              </v-tooltip>

              <v-tooltip location="bottom" content-class="custom-tooltip">
                <template #activator="{ props }">
                  <v-btn v-bind="props" size="small" variant="outlined" color="red" class="action-btn"
                    @click="openDeleteDialog(item)">
                    <v-icon size="16">mdi-delete</v-icon>
                  </v-btn>
                </template>
                {{ $t('general.delete') }}
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dijalog za dodavanje/uređivanje organizacione jedinice -->
    <BaseModal v-model="dialog"
      :title="editMode ? this.$t('modalni.orgJedinica.uredi') : this.$t('modalni.orgJedinica.dodaj')">
      <v-form ref="departmentPageForm" v-model="isFormValid">
        <v-text-field v-model="form.name" :label="$t('table.orgJedinica.naziv')" :rules="[rules.required]"
          class="my-text" required />
      </v-form>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="closeDialog">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.cancel') }}</span>
          </v-btn>

          <!-- SAČUVAJ -->
          <v-btn color="primary" variant="flat" @click="saveDepartment">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.save') }}</span>
          </v-btn>
        </div>
      </template>

    </BaseModal>

    <!-- Dijalog za potvrdu brisanja organizacione jedinice -->
    <BaseModal v-model="deleteDialog" :title="this.$t('modalni.orgJedinica.brisi')">
      <p>{{ $t('User_message.m85') }} <strong>{{ deleteDepartmentName }}</strong>?</p>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="deleteDialog = false">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.cancel') }}</span>
          </v-btn>

          <!-- OBRIŠI -->
          <v-btn color="danger" variant="flat" @click="confirmDelete">
            <span style="font-size:14px; font-weight: 600; color: #FFF">{{ $t('general.deleteButton') }}</span>
          </v-btn>
        </div>
      </template>

    </BaseModal>

    <!--Save/Print dialog-->
    <BaseModal v-model="reportPreview" size="xl" :title="$t('print.listaOrgJedinica')" height="90vh"
      modalClass="report-modal" :hideFooter="true">

      <template #header-actions>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.download')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="downloadPDF">
              <v-icon size="18"
                style="padding-top: 2px !important; padding-left: 10px !important;">mdi-download</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.print')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="printReport">
              <v-icon size="18">mdi-printer</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

      </template>

      <iframe ref="reportFrame" :style="{
        width: '100%',
        height: '100%',
        border: 'none',
        transform: `scale(${zoom})`,
        transformOrigin: 'top left'
      }" />

    </BaseModal>

  </v-container>
</template>

<script>
import BaseModal from "@/components/dialogs/BaseModal.vue";
import { printTable, fetchHeaderImage } from "@/services/printService";
import { format } from "date-fns";
import html2pdf from "html2pdf.js"

export default {
  name: "DepartmentsPage",
  data() {
    return {
      html: '',
      zoom: 1,
      reportPreview: false,
      deleteDialog: false,
      deleteDepartmentId: null,
      deleteDepartmentName: "",
      departments: [],
      loading: false,
      dialog: false,
      editMode: false,
      selectedId: null,
      isFormValid: false,
      search: "",
      rules: {
        required: v => !!v || this.$t('systemMessage.m1'),
      },
      form: {
        name: "",
      },
      headers: [
        { text: this.$t('table.orgJedinica.naziv'), value: "Name", sortable: true, width: 'calc(100% - 100px)' },
        { text: this.$t('general.optionsUPPER'), value: "actions", sortable: false, width: '100px' }
      ],
    };
  },

  computed: {
    filteredDepartments() {
      const term = this.search.toLowerCase().trim();

      const filtered = this.departments.filter(dep =>
        Object.values(dep).some(val =>
          String(val ?? "").toLowerCase().includes(term)
        )
      );
      return filtered;
    },
  },

  components: {
    BaseModal,
  },

  mounted() {
    this.fetchDepartments();
  },
  methods: {
    generateReportFileName(title) {

      const date = new Date()

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()

      const compactDate = `${day}${month}${year}`

      const cleanTitle = title
        .replace(/[čć]/g, "c")
        .replace("ž", "z")
        .replace("š", "s")
        .replace("đ", "d")
        .split(" ")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join("")

      return `${cleanTitle}_${compactDate}.pdf`
    },
    openReportPreview(html) {

      this.reportPreview = true

      this.$nextTick(() => {
        this.$nextTick(() => {

          const iframe = this.$refs.reportFrame
          if (!iframe) return

          const doc = iframe.contentWindow.document

          doc.open()
          doc.write(html)
          doc.close()

        })
      })
    },

    printReport() {
      const iframe = this.$refs.reportFrame
      iframe.contentWindow.print()
    },

    downloadPDF() {

      const container = document.createElement("div")
      container.innerHTML = this.html

      const title = container.querySelector("h2")?.innerText

      const fileName = this.generateReportFileName(title)

      html2pdf()
        .set({
          margin: 10,
          filename: fileName,
          html2canvas: { scale: 2 },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "landscape"
          }
        })
        .from(container)
        .save()
    },
    async printDepartments() {
      const departments = this.filteredDepartments || []

      if (!departments.length) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t("print.na"),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550",
        })
        return
      }

      const headerImg = await fetchHeaderImage(this.$axios)

      this.html = printTable({
        title: this.$t("print.listaOrgJedinica"),
        columns: [
          { key: "name", label: this.$t("table.orgJedinica.naziv"), width: "100%" }
        ],
        data: departments.map(d => ({
          name: d.Name ?? "-"
        })),
        headerImg,
        footer: `${this.$t("exportToExcel.pokrenuo")} ${localStorage.getItem("Name")} <br />
    ${this.$t("print.m1")} ${format(new Date(), "dd.MM.yyyy.")}`,
      })
      this.openReportPreview(this.html)
    },
    openDeleteDialog(department) {
      this.deleteDepartmentId = department.Id;
      this.deleteDepartmentName = department.Name;
      this.deleteDialog = true;
    },

    cancelDelete() {
      this.deleteDialog = false;
      this.deleteDepartmentId = null;
      this.deleteDepartmentName = "";
    },

    async confirmDelete() {
      if (!this.deleteDepartmentId) return;

      try {
        const r = await this.$axios.put(
          'Role/Department/Delete/' + this.deleteDepartmentId
        );

        if (r.status === 200 || r.status === 204) {
          this.$store.dispatch("NOTIF_SUCCESS", {
            poruka: this.$t('User_message.m13'),
            position: "top-end",
            timer: 2500,
            action: "noreload"
          });

          await this.fetchDepartments();
        }

      } catch (err) {
        console.error(err);

        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('User_message.m14'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        });

      } finally {
        this.cancelDelete();
      }
    },
    async fetchDepartments() {
      this.loading = true;

      try {
        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }
        const r = await this.$axios.post('Role/Department/', data)

        this.departments = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.departments = []
      } finally {
        this.loading = false;
      }
    },
    addDepartment() {
      this.editMode = false;
      this.selectedId = null;
      this.form.name = "";
      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.departmentPageForm?.resetValidation()
      })
    },
    editDepartment(department) {
      this.editMode = true;
      this.selectedId = department.Id;
      this.form.name = department.Name;
      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.departmentPageForm?.resetValidation()
      })
    },

    async saveDepartment() {
      const { valid } = await this.$refs.departmentPageForm.validate() // valiadcija forme
      if (!valid) return
      var data = this.editMode
        ? { Id: this.selectedId, Name: this.form.name, CompanyId: Number(localStorage.getItem('CompanyId')) }
        : { Name: this.form.name, CompanyId: Number(localStorage.getItem('CompanyId')) }

      if (this.editMode) {//editovanje departmenta
        await this.$axios.put('Role/Department/Edit/', data)
          .then(r => {
            if (r.data == "UPDATED") {
              //PRIKAZ PORUKE
              this.$store.dispatch("NOTIF_SUCCESS", {
                poruka: this.$t('User_message.m11'),
                position: "top-end",
                timer: 2500,
                action: "noreload"
              })

              this.fetchDepartments();
            }
            else {
              //PRIKAZ PORUKE
              this.$store.dispatch("NOTIF_WARNING", {
                poruka: this.$t('User_message.m12'),
                position: "center",
                icon: "error",
                timer: 4500,
                width: "550"
              })
            }
          })
      } else { //dodavanje novog departmenta
        await this.$axios.post('Role/Department/Add/', data)
          .then(r => {
            if (r.data == "INSERTED") {
              //PRIKAZ PORUKE
              this.$store.dispatch("NOTIF_SUCCESS", {
                poruka: this.$t('User_message.m9'),
                position: "top-end",
                timer: 2500,
                action: "noreload"
              })

              this.fetchDepartments();
            }
            else {
              //PRIKAZ PORUKE
              this.$store.dispatch("NOTIF_WARNING", {
                poruka: this.$t('User_message.m10'),
                position: "center",
                icon: "error",
                timer: 4500,
                width: "550"
              })
            }
          })
      }
      this.dialog = false;
      this.fetchDepartments();
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();

      this.$nextTick(() => {
        this.$refs.departmentPageForm?.resetValidation()
      })
    },
    resetForm() {
      this.form = {
        name: "",
      };
      this.selectedId = null;
    }
  },
};
</script>
